import { useState, useEffect, useCallback } from 'react'
import { DEFAULT_PHARMACY_NOTE_CONFIG } from '../utils/pharmacyNoteUtils'

/**
 * Fetches and tracks the PharmacyNoteConfig for a given pharmacy.
 *
 * Internally manages `activePharmacyId` state so that imperative overrides
 * (via `fetchConfig`) and reactive prop-driven syncs share a single fetch
 * path and cannot race each other.
 *
 * - When `pharmacyId` prop changes the active ID is updated automatically.
 * - `fetchConfig(id)` overrides the active ID imperatively (e.g. when a
 *   template tied to a different pharmacy is selected).
 * - When the active ID is null/undefined the config resets to the default.
 *
 * @param {string|number|null} pharmacyId
 * @param {object} apiClasses
 * @returns {{ pharmacyNoteConfig: object, fetchConfig: Function }}
 */
export const usePharmacyNoteConfig = (pharmacyId, apiClasses) => {
    const [pharmacyNoteConfig, setPharmacyNoteConfig] = useState(DEFAULT_PHARMACY_NOTE_CONFIG)
    const [activePharmacyId, setActivePharmacyId] = useState(pharmacyId ?? null)

    useEffect(() => {
        setActivePharmacyId(pharmacyId ?? null)
    }, [pharmacyId])

    useEffect(() => {
        if (!activePharmacyId || !apiClasses?.PharmacyNoteConfigsAPI) {
            setPharmacyNoteConfig(DEFAULT_PHARMACY_NOTE_CONFIG)
            return
        }
        apiClasses.PharmacyNoteConfigsAPI.getByPharmacyId(activePharmacyId)
            .then(({ data }) => {
                if (data?.config) setPharmacyNoteConfig(data.config)
                else setPharmacyNoteConfig(DEFAULT_PHARMACY_NOTE_CONFIG)
            })
            .catch(() => setPharmacyNoteConfig(DEFAULT_PHARMACY_NOTE_CONFIG))
    }, [activePharmacyId, apiClasses])

    const fetchConfig = useCallback((id) => {
        setActivePharmacyId(id ?? null)
    }, [])

    return { pharmacyNoteConfig, fetchConfig }
}
